import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';

import { PageSeo } from '@/seo';
import { billingSummaryKey, fetchBillingSummary } from '@/shared/api';

import { AppHeader } from './components/AppHeader';
import { DashboardMobileNav } from './components/DashboardMobileNav';
import { isNavItemActive, SIDEBAR_LINKS } from './components/dashboardNav';
import { DashboardSidebar } from './components/DashboardSidebar';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { QuickActionSheet } from './components/QuickActionSheet';

/**
 * Chrome for the master dashboard: sidebar on desktop, bottom bar on mobile.
 *
 * Applied as a layout route, so the pages underneath render their content and
 * nothing else — previously each of the fourteen pages imported this component
 * and wrapped itself in it.
 */
export const DashboardLayout = () => {
  const { t } = useTranslation('app');
  const { pathname } = useLocation();
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);

  // The one place the plan is fetched: PlanProvider only observes this query,
  // so public pages never ask the API about a session.
  useQuery({ queryKey: billingSummaryKey, queryFn: fetchBillingSummary, staleTime: 60_000 });

  const activeLink = SIDEBAR_LINKS.find((link) => isNavItemActive(pathname, link.path));
  const title = activeLink ? t(activeLink.labelKey) : t('nav.dashboard');

  return (
    <div className="flex min-h-screen bg-surface text-left">
      <PageSeo page="app" />
      <DashboardSidebar />

      <DashboardMobileNav
        quickActionsOpen={quickActionsOpen}
        onToggleQuickActions={() => setQuickActionsOpen((open) => !open)}
      />
      <QuickActionSheet open={quickActionsOpen} onClose={() => setQuickActionsOpen(false)} />

      {/* min-w-0: a flex item defaults to its content's width, so one wide
          child (a tab row, a filter row, the week grid) widened every page
          to up to 753px on a 390px phone — each page's own overflow-x-auto
          never engaged. Found by gate A9's width check, not by a diff. */}
      <main className="flex min-h-screen min-w-0 flex-1 flex-col pb-24 md:ml-[260px] md:pb-8">
        <ImpersonationBanner />
        <AppHeader audience="master" title={title} />
        <Outlet />
      </main>
    </div>
  );
};
